import type { Namespace } from '../types';

// DE drafted — queued for native review in the task file's "DE review queue".
// "Grow Room" / "Rack" / "Plumbing Room" stay invariant (domain nouns; the spec
// HTML keeps "Grow Room" in its German copy). `{noun}` interpolations receive them.
export const mapper = {
  title: { de: 'Mapper', en: 'Mapper' },
  desc: {
    de: 'Grow Rooms, Racks und Plumbing Rooms verwalten — und welcher Mikrocontroller wo zugeordnet ist.',
    en: 'Manage grow rooms, racks, plumbing rooms, and which microcontroller is mapped to each.',
  },
  addGrowRoom: { de: 'Grow Room hinzufügen', en: 'Add Grow Room' },
  addPlumbing: { de: 'Plumbing Room hinzufügen', en: 'Add Plumbing Room' },
  addRack: { de: 'Rack hinzufügen', en: 'Add rack' },
  addNode: { de: 'Node hinzufügen', en: 'Add node' },

  statGrowRooms: { de: 'Grow Rooms', en: 'Grow rooms' },
  statRacks: { de: 'Racks', en: 'Racks' },
  statPlumbing: { de: 'Plumbing Rooms', en: 'Plumbing rooms' },
  statNodes: { de: 'Nodes', en: 'Nodes' },

  loadError: { de: 'Farm-Karte konnte nicht geladen werden', en: "Couldn't load the farm map" },
  mgrUnreachable: {
    de: 'Der iot-manager-Dienst ist nicht erreichbar. Bitte gleich erneut versuchen.',
    en: 'The iot-manager service is unreachable. Retry shortly.',
  },
  emptyTitle: { de: 'Noch keine Räume', en: 'No rooms yet' },
  emptyMsg: {
    de: 'Räume, Racks und Nodes werden alle in der Standort-Hierarchie angelegt — Raum-Art einer Zone markieren, dann Unterzonen hinzufügen. Geräte werden hier zugeordnet.',
    en: "Rooms, racks and nodes are all created in the Location Hierarchy — tag a zone's kind, then add sub-zones. Devices are attached here.",
  },
  // R-149: the mapper is a pure view. Rooms come from tagging a zone's kind and
  // racks/nodes from adding sub-zones (Location Hierarchy); only devices attach here.
  roomsFromLocations: {
    de: 'Räume, Racks und Nodes werden in der Standort-Hierarchie angelegt (Raum-Art markieren, Unterzonen hinzufügen). Geräte werden hier zugeordnet.',
    en: "Rooms, racks and nodes are created in the Location Hierarchy (tag a zone's kind, add sub-zones). Devices are attached here.",
  },

  toastEnterCode: { de: 'Code eingeben', en: 'Enter a code' },
  toastNoParent: { de: 'Übergeordneter Grow Room nicht gefunden', en: 'Parent grow room not found' },
  toastRackAdded: { de: 'Rack {code} hinzugefügt', en: 'Rack {code} added' },
  toastNodeAdded: { de: 'Node {code} hinzugefügt', en: 'Node {code} added' },
  toastPlumbingAdded: { de: 'Plumbing Room {code} hinzugefügt', en: 'Plumbing room {code} added' },
  toastGrowAdded: { de: 'Grow Room {code} hinzugefügt', en: 'Grow room {code} added' },
  toastNoPlumbingParent: {
    de: 'Übergeordneter Plumbing Room nicht gefunden',
    en: 'Parent plumbing room not found',
  },
  toastAddFailed: { de: 'Hinzufügen fehlgeschlagen: {error}', en: "Couldn't add: {error}" },

  rackCount_one: { de: '{n} Rack', en: '{n} rack' },
  rackCount_many: { de: '{n} Racks', en: '{n} racks' },
  // R-190. Levels are sparse: the count is of INSTRUMENTED shelves, not the rack's capacity, so the
  // wording must not read as "this rack has 2 shelves".
  levelCount_one: { de: '{n} bestückte Ebene', en: '{n} instrumented level' },
  levelCount_many: { de: '{n} bestückte Ebenen', en: '{n} instrumented levels' },
  rackField: { de: 'Rack', en: 'Rack' },
  levelIndexField: { de: 'Ebene (Position)', en: 'Level (position)' },
  levelNoneHint: {
    de: 'Keine bestückte Ebene — Ebenen entstehen erst, wenn ein Controller darauf sitzt.',
    en: 'No instrumented level — a level exists only once a controller sits on it.',
  },
  nodeCount_one: { de: '{n} Node', en: '{n} node' },
  nodeCount_many: { de: '{n} Nodes', en: '{n} nodes' },
  plumbingRoom: { de: 'Plumbing Room', en: 'Plumbing room' },
  noMc: { de: 'Kein Mikrocontroller', en: 'No microcontroller' },
  // R-350: the loop that waters a grow room, and what a plumbing room feeds.
  fedBy: { de: 'versorgt von {code}', en: 'fed by {code}' },
  fedByNone: { de: 'kein Kreislauf zugeordnet', en: 'no loop mapped' },
  feeds: { de: 'versorgt {codes}', en: 'feeds {codes}' },
  feedsNone: { de: 'versorgt keinen Grow Room', en: 'feeds no grow room' },
  movesFrom: { de: 'wechselt von {code}', en: 'moves from {code}' },

  detailTitle: { de: '{noun} {code}', en: '{noun} {code}' },
  editTitle: { de: '{noun} {code} bearbeiten', en: 'Edit {noun} {code}' },
  deleteTitle: { de: '{code} löschen?', en: 'Delete {code}?' },
  deleteNoun: { de: '{noun} löschen', en: 'Delete {noun}' },
  toastUpdated: { de: '{noun} {code} aktualisiert', en: '{noun} {code} updated' },
  toastDeleted: { de: '{noun} {code} gelöscht', en: '{noun} {code} deleted' },
  toastDeleteFailed: { de: '{code} kann nicht gelöscht werden: {error}', en: "Can't delete {code}: {error}" },
  itemGone: {
    de: 'Dieses Element existiert nicht mehr — bitte neu laden.',
    en: 'This item no longer exists — reload.',
  },
  saveFailed: { de: 'Speichern fehlgeschlagen: {error}', en: 'Save failed: {error}' },
  saveMsgPrefix: { de: 'Änderungen übernehmen für', en: 'Apply your changes to' },

  moveTitle: { de: 'Controller verschieben?', en: 'Move controller?' },
  moveMsg1: { de: 'ist derzeit auf', en: 'is currently on' },
  // Leading '.' joins directly after the bolded code in both locales.
  moveMsg2: {
    de: '. Beim Speichern wird er hierher verschoben.',
    en: '. It will be moved here when you save changes.',
  },
  moveConfirm: { de: 'OK, auswählen', en: 'OK, select it' },

  deleteRackMsgPrefix: { de: 'Dies entfernt das Rack', en: 'This removes the rack' },
  deleteNodeMsgPrefix: { de: 'Dies entfernt den Node', en: 'This removes the node' },
  deletePlumbingMsgPrefix: { de: 'Dies entfernt den Plumbing Room', en: 'This removes the plumbing room' },
  deleteMsgSuffix: {
    de: 'von der Karte. Heben Sie zuerst die Mikrocontroller-Zuweisung auf; der Zuweisungsverlauf bleibt erhalten.',
    en: 'from the map. Unassign its microcontroller first; the assignment history is kept.',
  },
  deleteGrowMsgPrefix: { de: 'Dies entfernt den Grow Room', en: 'This removes the grow room' },
  deleteGrowMsgSuffix: {
    de: 'und seine Racks von der Karte. Heben Sie zuerst alle Mikrocontroller-Zuweisungen auf; der Zuweisungsverlauf bleibt erhalten.',
    en: 'and its racks from the map. Unassign any microcontrollers first; the assignment history is kept.',
  },
  deletePlumbingMsgSuffix: {
    de: 'und seine Nodes von der Karte. Heben Sie zuerst alle Mikrocontroller-Zuweisungen auf; der Zuweisungsverlauf bleibt erhalten.',
    en: 'and its nodes from the map. Unassign any microcontrollers first; the assignment history is kept.',
  },

  secMicrocontroller: { de: 'Mikrocontroller', en: 'Microcontroller' },
  secParentMc: { de: 'Übergeordneter Mikrocontroller', en: 'Parent microcontroller' },
  secMicrocontrollers: { de: 'Mikrocontroller', en: 'Microcontrollers' },
  secRacks: { de: 'Racks', en: 'Racks' },
  noRacksYet: { de: 'Noch keine Racks.', en: 'No racks yet.' },
  secNodes: { de: 'Nodes', en: 'Nodes' },
  noNodesYet: { de: 'Noch keine Nodes.', en: 'No nodes yet.' },
  growRoomField: { de: 'Grow Room', en: 'Grow room' },
  plumbingRoomField: { de: 'Plumbing Room', en: 'Plumbing room' },
  rackNoMove: { de: 'Ein Rack kann den Raum nicht wechseln.', en: "A rack can't move between rooms." },
  rackNoMoveHint: {
    de: 'Ein Rack kann den Raum nicht wechseln — zum Verschieben löschen und neu anlegen.',
    en: "A rack can't move between rooms — delete and re-add to relocate it.",
  },
  nodeNoMove: { de: 'Ein Node kann den Raum nicht wechseln.', en: "A node can't move between rooms." },
  nodeNoMoveHint: {
    de: 'Ein Node kann den Raum nicht wechseln — zum Verschieben löschen und neu anlegen.',
    en: "A node can't move between rooms — delete and re-add to relocate it.",
  },
  codeField: { de: '{noun}-Code', en: '{noun} code' },
  codePlaceholder: { de: '{noun}-Code eingeben', en: 'Enter the {noun} code' },
  codeHint: { de: 'Ein kurzer, eindeutiger Code.', en: 'A short, unique code.' },

  // Location-tree binding (R-135).
  fieldLocation: { de: 'Standort', en: 'Location' },
  selectLocation: { de: '— Standort wählen —', en: '— select location —' },
  derivedCodeHint: {
    de: 'Der Code wird aus dem gewählten Standort übernommen.',
    en: 'The code is derived from the selected location.',
  },
  relinkRenames: {
    de: 'Neu verknüpfen benennt den Raum um — der Code wird zum Standort-Code.',
    en: 'Re-linking renames the room — the code becomes the location code.',
  },

  addMc: { de: 'Mikrocontroller hinzufügen', en: 'Add microcontroller' },
  changeMc: { de: 'Mikrocontroller wechseln', en: 'Change microcontroller' },
  searchMcs: { de: 'Mikrocontroller suchen…', en: 'Search microcontrollers…' },
  groupAssignedElsewhere: { de: 'Anderweitig zugewiesen', en: 'Assigned elsewhere' },
  onCode: { de: 'auf {code}', en: 'on {code}' },

  // Add menu + room search/filter (R-098).
  add: { de: 'Hinzufügen', en: 'Add' },
  addNewMc: { de: 'Neuen Mikrocontroller anlegen', en: 'Add new microcontroller' },
  searchRooms: { de: 'Räume suchen…', en: 'Search rooms…' },
  filterAllRooms: { de: 'Alle Räume', en: 'All rooms' },
  filterGrowRooms: { de: 'Grow Rooms', en: 'Grow rooms' },
  filterPlumbingRooms: { de: 'Plumbing Rooms', en: 'Plumbing rooms' },
  filterSearchType: { de: 'Raumtyp suchen…', en: 'Search room type…' },
  noMatchTitle: { de: 'Keine Treffer', en: 'No matches' },
  noMatchMsg: { de: 'Kein Raum passt zur Suche/zum Filter.', en: 'No room matches the search / filter.' },

  // ── Unassigned tray (R-226) ──
  // A detected board that sits in no location appears on NO device screen, because those
  // screens enumerate locations. The copy has to say that plainly — "not assigned" alone
  // does not tell an operator why they could not find it.
  unassignedTitle: { de: 'Nicht zugeordnete Controller', en: 'Unassigned controllers' },
  unassignedHint: {
    de: 'Diese Controller melden sich, sind aber keinem Standort zugeordnet — deshalb erscheinen sie auf keinem Geräte-Bildschirm. Ordnen Sie sie einem Raum, Rack, einer Ebene oder einem Node zu.',
    en: 'These controllers are detected but assigned to no location — which is why they appear on no device screen. Assign each to a room, rack, level or node.',
  },
  unassignedReporting: { de: 'Meldet sich gerade', en: 'Reporting now' },
  unassignedSilent: { de: 'Meldet sich nicht mehr', en: 'No longer reporting' },
  unassignedChannels: { de: '{s} Sensoren · {a} Aktoren', en: '{s} sensors · {a} actuators' },
  unassignedAssign: { de: 'Zuordnen', en: 'Assign' },
  unassignedAssignTitle: { de: '{mac} zuordnen', en: 'Assign {mac}' },
  unassignedSlotField: { de: 'Standort', en: 'Location' },
  // Says why the list may look short — an occupied slot is absent by design, not missing.
  unassignedSlotHint: {
    de: 'Nur freie Standorte. Um einen belegten Standort zu ändern, öffnen Sie ihn und wechseln Sie dort den Controller.',
    en: 'Free locations only. To change an occupied one, open that location and swap its controller there.',
  },
  unassignedNoSlots: {
    de: 'Jeder Standort ist bereits belegt. Legen Sie zuerst in der Standort-Hierarchie ein Rack, eine Ebene oder einen Node an.',
    en: 'Every location is already occupied. Add a rack, level or node in the Location Hierarchy first.',
  },
  unassignedSlotGone: {
    de: 'Dieser Standort ist nicht mehr frei — bitte neu laden.',
    en: 'That location is no longer free — reload.',
  },
  unassignedAssigned: { de: '{mac} zu {code} zugeordnet', en: '{mac} assigned to {code}' },

  // Shown on the Sensors/Actuators screens. It must say the readings are MISSING FROM
  // THIS SCREEN — "unassigned controller" alone reads as an admin detail, and the
  // operator's actual question is why a board they installed shows nothing.
  unmappedBanner_one: {
    de: '1 Controller meldet Messwerte, ist aber keinem Standort zugeordnet — seine Kanäle fehlen auf dieser Seite.',
    en: '1 controller is reporting readings but is assigned to no location — its channels are missing from this page.',
  },
  unmappedBanner_many: {
    de: '{n} Controller melden Messwerte, sind aber keinem Standort zugeordnet — ihre Kanäle fehlen auf dieser Seite.',
    en: '{n} controllers are reporting readings but are assigned to no location — their channels are missing from this page.',
  },
  unmappedBannerCta: { de: 'Im Mapper zuordnen', en: 'Assign in the Mapper' },
} as const satisfies Namespace;
